import { Controller } from "@hotwired/stimulus";
import "./component.scss";
import { DirectUpload } from "@rails/activestorage";
import { Dropzone } from "dropzone";
import { refreshCsrfToken } from "../../helpers/csrf";
import { setUploadBlocking } from "../../helpers/upload_submit_lock";

// compare https://gist.github.com/lazaronixon/dca1b48c241422d6347f4b0c93bec739

const uploadedFileSize = (size) => {
  let selectedSize = 0;

  if (size > 0) {
    selectedSize = size / 1000000;
  }

  return selectedSize.toFixed(2);
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1));
  return `${size}${sizes[i]}`;
};

const truncateFilename = (filename, maxLength = 15) => {
  if (filename.length <= maxLength) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    // No extension, just truncate
    return filename.substring(0, maxLength - 3) + '...';
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExt = filename.substring(0, lastDotIndex);
  const availableLength = maxLength - extension.length - 3; // 3 for "..."

  if (nameWithoutExt.length <= availableLength) {
    return filename;
  }

  return nameWithoutExt.substring(0, availableLength) + '...' + extension;
};

const documentLabel = (file) => {
  const lastDotIndex = file.name.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return "FILE";
  }

  return file.name.substring(lastDotIndex + 1).toUpperCase();
};

const renderPdfPreview = (dzImage, file) => {
  const truncatedName = truncateFilename(file.name, 20);
  const fileSize = formatFileSize(file.size);

  dzImage.classList.add("dz-image--pdf");
  dzImage.innerHTML = `
    <svg class="dz-pdf-icon" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path style="color: #4e38f8" d="M31.6667 0H8.33333C3.73096 0 0 3.73096 0 8.33333V31.6667C0 36.269 3.73096 40 8.33333 40H31.6667C36.269 40 40 36.269 40 31.6667V8.33333C40 3.73096 36.269 0 31.6667 0Z" fill="currentColor"/>
      <path d="M10.5087 22.2555V25.5953H8.55554V16.1161H12.2535C12.9653 16.1161 13.5903 16.2463 14.1285 16.5067C14.671 16.7671 15.0877 17.1383 15.3785 17.62C15.6693 18.0975 15.8147 18.6421 15.8147 19.2541C15.8147 20.183 15.4957 20.9165 14.8576 21.4546C14.2239 21.9885 13.345 22.2555 12.2209 22.2555H10.5087ZM10.5087 20.6735H12.2535C12.77 20.6735 13.1628 20.5518 13.4318 20.3088C13.7053 20.0658 13.842 19.7185 13.842 19.2671C13.842 18.8028 13.7053 18.4273 13.4318 18.1408C13.1584 17.8543 12.7808 17.7068 12.299 17.6981H10.5087V20.6735ZM17.1624 25.5953V16.1161H20.079C20.9124 16.1161 21.6567 16.3049 22.312 16.6825C22.9719 17.0558 23.486 17.5896 23.855 18.2841C24.224 18.9741 24.4084 19.7598 24.4084 20.6408V21.077C24.4084 21.9581 24.2262 22.7415 23.8615 23.4273C23.5014 24.1131 22.9914 24.6448 22.3315 25.0223C21.6719 25.4 20.9275 25.591 20.0985 25.5953H17.1624ZM19.1155 17.6981V24.0263H20.0595C20.8234 24.0263 21.4072 23.7766 21.8107 23.2776C22.2144 22.7785 22.4205 22.0645 22.4292 21.1356V20.6343C22.4292 19.6708 22.2295 18.9416 21.8304 18.4468C21.431 17.9476 20.8472 17.6981 20.079 17.6981H19.1155ZM31.5374 21.7216H27.7874V25.5953H25.8342V16.1161H32.006V17.6981H27.7874V20.146H31.5374V21.7216Z" fill="#FFFFFF"/>
    </svg>
  `;
  appendPreviewText(dzImage, "dz-pdf-name label--medium", truncatedName, true);
  appendPreviewText(dzImage, "dz-pdf-meta label--small", `PDF • ${fileSize}`);
  dzImage.classList.add("loaded");
};

const renderDocumentPreview = (dzImage, file) => {
  const truncatedName = truncateFilename(file.name, 20);
  const fileSize = formatFileSize(file.size);
  const label = documentLabel(file);

  dzImage.classList.add("dz-image--document");
  dzImage.innerHTML = `
    <svg class="dz-document-icon" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path style="color: #4e38f8" d="M31.6667 0H8.33333C3.73096 0 0 3.73096 0 8.33333V31.6667C0 36.269 3.73096 40 8.33333 40H31.6667C36.269 40 40 36.269 40 31.6667V8.33333C40 3.73096 36.269 0 31.6667 0Z" fill="currentColor"/>
      <g transform="translate(8 8)">
        <path d="M6 22C5.45 22 4.97917 21.8042 4.5875 21.4125C4.19583 21.0208 4 20.55 4 20V4C4 3.45 4.19583 2.97917 4.5875 2.5875C4.97917 2.19583 5.45 2 6 2H13.175C13.4417 2 13.6958 2.05 13.9375 2.15C14.1792 2.25 14.3917 2.39167 14.575 2.575L19.425 7.425C19.6083 7.60833 19.75 7.82083 19.85 8.0625C19.95 8.30417 20 8.55833 20 8.825V20C20 20.55 19.8042 21.0208 19.4125 21.4125C19.0208 21.8042 18.55 22 18 22H6ZM13 8V4H6V20H18V9H14C13.7167 9 13.4792 8.90417 13.2875 8.7125C13.0958 8.52083 13 8.28333 13 8Z" fill="#FFFFFF"/>
      </g>
    </svg>
  `;
  appendPreviewText(dzImage, "dz-document-name label--medium", truncatedName, true);
  appendPreviewText(dzImage, "dz-document-meta label--small", `${label} • ${fileSize}`);
  dzImage.classList.add("loaded");
};

const usesGenericDocumentPreview = (file, previewTemplate) =>
  previewTemplate === "document" ||
  file.type?.startsWith("application/") ||
  file.type === "text/calendar";

const appendPreviewText = (container, className, text, isFilename = false) => {
  const element = document.createElement("span");
  element.className = className;
  element.textContent = text;
  if (isFilename) element.dataset.dzName = "";
  container.appendChild(element);
};

export default class extends Controller {
  static targets = ["cameraInput"];
  static values = {
    url: String,
    paramName: String,
    deletionParamName: { type: String, default: null },
    videosDeletionParamName: { type: String, default: null },
    maxFiles: { type: Number, default: null },
    maxFilesize: { type: Number, default: 256 },
    acceptedFiles: { type: String, default: null },
    addRemoveLinks: { type: Boolean, default: true },
    customClickable: { type: String, default: "" },
    existingFilesUrls: { type: Array, default: [] },
    finishedUploadText: { type: String, default: "Upload finished." },
    previewTemplate: { type: String, default: "default" },
    cameraClickable: { type: String, default: "" },
    dictFileTooBig: {
      type: String,
      default:
        "File is too big ({{filesize}}MiB). Max filesize: {{maxFilesize}}MiB.",
    },
    dictMaxFilesExceeded: {
      type: String,
      default: "You can not upload any more files.",
    },
  };

  connect() {
    this.existingFiles = new Map(); // Track existing files by their URL
    this.dropZone = this.#createDropZone();
    this.dropZone.enqueueFile = (it) => this.#upload(it);

    // Listen for file removal events
    this.dropZone.on("removedfile", (file) => this.#handleFileRemoval(file));

    // Listen for max files exceeded event
    this.dropZone.on("maxfilesexceeded", (file) =>
      this.#handleMaxFilesExceeded(file)
    );

    // Add listeners to toggle the add button visibility and update counter
    this.dropZone.on("addedfile", () => {
      this.#updateAddButtonVisibility();
      this.#updateCounter();
    });
    this.dropZone.on("removedfile", () => {
      this.#updateAddButtonVisibility();
      this.#updateCounter();
    });

    // Access submit button target (if present)
    this.submitButton = document.querySelector(
      "[data-js='dropzone-submit']"
    );

    // Saving is blocked while a new file is uploading or failed (incl. rejected files)
    ["addedfile", "processing", "success", "error", "removedfile"].forEach((event) =>
      this.dropZone.on(event, () => this.#syncUploadBlocking())
    );

    // Find the button within THIS dropzone's closest parent container
    // The button is a sibling to the dropzone, so we search in the parent
    const parentContainer = this.element.closest(
      ".info-gallery-items__content-field, .new-post__content-field"
    );
    this.addButton = parentContainer?.querySelector(
      ".js-dropzone-add-files"
    );
    this.counterElement = parentContainer?.parentElement?.querySelector(
      ".js-dropzone-counter"
    );

    this.#updateAddButtonVisibility();
    this.#updateCounter();
    this.#setupCamera();
  }

  disconnect() {
    this.#teardownCamera();
    this.dropZone.destroy();
  }

  // Wire camera trigger button(s) to a capture input and feed shots into Dropzone
  #setupCamera() {
    if (!this.cameraClickableValue || !this.hasCameraInputTarget) return;

    this.boundOpenCamera = () => this.cameraInputTarget.click();
    this.cameraTriggers = document.querySelectorAll(this.cameraClickableValue);
    this.cameraTriggers.forEach((el) =>
      el.addEventListener("click", this.boundOpenCamera)
    );

    this.boundCapture = (event) => this.#handleCapturedFiles(event);
    this.cameraInputTarget.addEventListener("change", this.boundCapture);
  }

  #teardownCamera() {
    this.cameraTriggers?.forEach((el) =>
      el.removeEventListener("click", this.boundOpenCamera)
    );
    if (this.hasCameraInputTarget && this.boundCapture) {
      this.cameraInputTarget.removeEventListener("change", this.boundCapture);
    }
  }

  #handleCapturedFiles(event) {
    Array.from(event.target.files || []).forEach((file) =>
      this.dropZone.addFile(file)
    );
    // Reset so capturing the same shot again still fires a change event
    event.target.value = "";
  }

  #createDropZone() {
    const previewTemplate = document.querySelector(
      `#dz-${this.previewTemplateValue}`
    )?.innerHTML;

    const dropZone = new Dropzone(this.element, {
      url: this.urlValue,
      paramName: this.paramNameValue,
      maxFiles: this.maxFilesValue,
      maxFilesize: this.maxFilesizeValue,
      acceptedFiles: this.acceptedFilesValue,
      addRemoveLinks: this.addRemoveLinksValue,
      uploadMultiple: true,
      clickable: (this.hasCustomClickableValue && !!this.customClickableValue) ? this.customClickableValue : true,
      previewTemplate: previewTemplate, // Keep as string
      dictFileTooBig: this.dictFileTooBigValue,
      dictMaxFilesExceeded: this.dictMaxFilesExceededValue,
      accept: (file, done) => {
        const currentFileCount = this.dropZone.files.length;

        if (this.maxFilesValue && currentFileCount > this.maxFilesValue) {
          this.#showGlobalError(this.dictMaxFilesExceededValue);
          done(this.dictMaxFilesExceededValue);
          return;
        }
        done();
      },
      uploadprogress: (file, progress, bytesSent, _totalBytes) => {
        if (file.previewElement) {
          for (let node of file.previewElement.querySelectorAll(
            "[data-dz-uploadprogress]"
          )) {
            node.style.width = `${progress}%`;
          }
          for (let node of file.previewElement.querySelectorAll(
            "[data-dz-data]"
          )) {
            if (progress === 100) {
              node.innerHTML = this.finishedUploadTextValue;
            } else {
              const uploadedSize = uploadedFileSize(bytesSent);
              const totalSize = uploadedFileSize(file.size);
              node.innerHTML = `${uploadedSize} / ${totalSize} MB`;
            }
          }
        }
      },
      dictCancelUpload:
        document.querySelector(`#dz-${this.previewTemplateValue}-cancel-upload`)
          ?.innerHTML ||
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.9998 13.4L7.0998 18.3C6.91647 18.4833 6.68314 18.575 6.3998 18.575C6.11647 18.575 5.88314 18.4833 5.6998 18.3C5.51647 18.1167 5.4248 17.8833 5.4248 17.6C5.4248 17.3167 5.51647 17.0833 5.6998 16.9L10.5998 12L5.6998 7.09999C5.51647 6.91665 5.4248 6.68332 5.4248 6.39999C5.4248 6.11665 5.51647 5.88332 5.6998 5.69999C5.88314 5.51665 6.11647 5.42499 6.3998 5.42499C6.68314 5.42499 6.91647 5.51665 7.0998 5.69999L11.9998 10.6L16.8998 5.69999C17.0831 5.51665 17.3165 5.42499 17.5998 5.42499C17.8831 5.42499 18.1165 5.51665 18.2998 5.69999C18.4831 5.88332 18.5748 6.11665 18.5748 6.39999C18.5748 6.68332 18.4831 6.91665 18.2998 7.09999L13.3998 12L18.2998 16.9C18.4831 17.0833 18.5748 17.3167 18.5748 17.6C18.5748 17.8833 18.4831 18.1167 18.2998 18.3C18.1165 18.4833 17.8831 18.575 17.5998 18.575C17.3165 18.575 17.0831 18.4833 16.8998 18.3L11.9998 13.4Z" fill="currentColor"/></svg>',
      dictRemoveFile:
        document.querySelector(`#dz-${this.previewTemplateValue}-remove-file`)
          ?.innerHTML ||
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.9998 13.4L7.0998 18.3C6.91647 18.4833 6.68314 18.575 6.3998 18.575C6.11647 18.575 5.88314 18.4833 5.6998 18.3C5.51647 18.1167 5.4248 17.8833 5.4248 17.6C5.4248 17.3167 5.51647 17.0833 5.6998 16.9L10.5998 12L5.6998 7.09999C5.51647 6.91665 5.4248 6.68332 5.4248 6.39999C5.4248 6.11665 5.51647 5.88332 5.6998 5.69999C5.88314 5.51665 6.11647 5.42499 6.3998 5.42499C6.68314 5.42499 6.91647 5.51665 7.0998 5.69999L11.9998 10.6L16.8998 5.69999C17.0831 5.51665 17.3165 5.42499 17.5998 5.42499C17.8831 5.42499 18.1165 5.51665 18.2998 5.69999C18.4831 5.88332 18.5748 6.11665 18.5748 6.39999C18.5748 6.68332 18.4831 6.91665 18.2998 7.09999L13.3998 12L18.2998 16.9C18.4831 17.0833 18.5748 17.3167 18.5748 17.6C18.5748 17.8833 18.4831 18.1167 18.2998 18.3C18.1165 18.4833 17.8831 18.575 17.5998 18.575C17.3165 18.575 17.0831 18.4833 16.8998 18.3L11.9998 13.4Z" fill="currentColor"/></svg>',
    });

    this.existingFilesUrlsValue.forEach((file) => {
      const mockFile = {
        ...file.options,
        accepted: true,
      };

      this.existingFiles.set(file.url, mockFile);
      dropZone.files.push(mockFile);
      dropZone.displayExistingFile(mockFile, file.url, null, '*', false);
    });

    setTimeout(() => {
      dropZone.files.forEach((file) => {
        if (file.previewElement) {
          const dzImage = file.previewElement.querySelector(".dz-image");
          const img = dzImage?.querySelector("img");

          if (file.type === "application/pdf" && dzImage) {
            renderPdfPreview(dzImage, file);
          } else if (usesGenericDocumentPreview(file, this.previewTemplateValue) && dzImage) {
            renderDocumentPreview(dzImage, file);
          } else if (img && dzImage) {
            // Check if this is an existing video file
            if (file.type && file.type.startsWith("video/")) {
              dzImage.classList.add("dz-image--existing-video");

              // Add overlay wrapper similar to play-icon-wrapper
              if (!dzImage.querySelector(".dz-image__overlay")) {
                const overlay = document.createElement("div");
                overlay.className = "dz-image__overlay";
                overlay.innerHTML = `
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18H13C13.2833 18 13.5208 17.9042 13.7125 17.7125C13.9042 17.5208 14 17.2833 14 17V16L15.275 16.675C15.4417 16.7583 15.6042 16.75 15.7625 16.65C15.9208 16.55 16 16.4083 16 16.225V13.775C16 13.5917 15.9208 13.45 15.7625 13.35C15.6042 13.25 15.4417 13.2417 15.275 13.325L14 14V13C14 12.7167 13.9042 12.4792 13.7125 12.2875C13.5208 12.0958 13.2833 12 13 12H9C8.71667 12 8.47917 12.0958 8.2875 12.2875C8.09583 12.4792 8 12.7167 8 13V17C8 17.2833 8.09583 17.5208 8.2875 17.7125C8.47917 17.9042 8.71667 18 9 18ZM6 22C5.45 22 4.97917 21.8042 4.5875 21.4125C4.19583 21.0208 4 20.55 4 20V4C4 3.45 4.19583 2.97917 4.5875 2.5875C4.97917 2.19583 5.45 2 6 2H13.175C13.4417 2 13.6958 2.05 13.9375 2.15C14.1792 2.25 14.3917 2.39167 14.575 2.575L19.425 7.425C19.6083 7.60833 19.75 7.82083 19.85 8.0625C19.95 8.30417 20 8.55833 20 8.825V20C20 20.55 19.8042 21.0208 19.4125 21.4125C19.0208 21.8042 18.55 22 18 22H6ZM13 8V4H6V20H18V9H14C13.7167 9 13.4792 8.90417 13.2875 8.7125C13.0958 8.52083 13 8.28333 13 8Z" fill="currentColor"/>
                  </svg>
                `;
                dzImage.appendChild(overlay);
              }
            }

            const markAsLoaded = () => {
              img.classList.add("loaded");
              dzImage.classList.add("loaded");
            };

            if (img.complete && img.naturalHeight !== 0) {
              markAsLoaded();
            } else {
              img.addEventListener("load", markAsLoaded);
            }
          }
        }
      });
    }, 100);

    dropZone.on("thumbnail", (file, dataUrl) => {
      if (file.previewElement) {
        const dzImage =
          file.previewElement.querySelector(".dz-image");
        const img = dzImage?.querySelector("img");
        if (img && dzImage) {
          const markAsLoaded = () => {
            setTimeout(() => {
              img.classList.add("loaded");
              dzImage.classList.add("loaded");
            }, 300);
          };

          if (img.complete && img.naturalHeight !== 0) {
            markAsLoaded();
          } else {
            img.addEventListener("load", markAsLoaded);
          }
        }
      }
    });

    // Add handler for video files after preview is created
    dropZone.on("addedfile", (file) => {
      if (file.type.startsWith("video/") && file.previewElement) {
        const dzImage = file.previewElement.querySelector(".dz-image");
        if (dzImage) {
          const truncatedName = truncateFilename(file.name);
          dzImage.classList.add("dz-image--video");
          dzImage.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18H13C13.2833 18 13.5208 17.9042 13.7125 17.7125C13.9042 17.5208 14 17.2833 14 17V16L15.275 16.675C15.4417 16.7583 15.6042 16.75 15.7625 16.65C15.9208 16.55 16 16.4083 16 16.225V13.775C16 13.5917 15.9208 13.45 15.7625 13.35C15.6042 13.25 15.4417 13.2417 15.275 13.325L14 14V13C14 12.7167 13.9042 12.4792 13.7125 12.2875C13.5208 12.0958 13.2833 12 13 12H9C8.71667 12 8.47917 12.0958 8.2875 12.2875C8.09583 12.4792 8 12.7167 8 13V17C8 17.2833 8.09583 17.5208 8.2875 17.7125C8.47917 17.9042 8.71667 18 9 18ZM6 22C5.45 22 4.97917 21.8042 4.5875 21.4125C4.19583 21.0208 4 20.55 4 20V4C4 3.45 4.19583 2.97917 4.5875 2.5875C4.97917 2.19583 5.45 2 6 2H13.175C13.4417 2 13.6958 2.05 13.9375 2.15C14.1792 2.25 14.3917 2.39167 14.575 2.575L19.425 7.425C19.6083 7.60833 19.75 7.82083 19.85 8.0625C19.95 8.30417 20 8.55833 20 8.825V20C20 20.55 19.8042 21.0208 19.4125 21.4125C19.0208 21.8042 18.55 22 18 22H6ZM13 8V4H6V20H18V9H14C13.7167 9 13.4792 8.90417 13.2875 8.7125C13.0958 8.52083 13 8.28333 13 8Z" fill="#6A687F"/>
            </svg>
            <span class="label--medium" data-dz-name>${truncatedName}</span>
          `;
          dzImage.classList.add("loaded");
        }
      }

      if (file.type === "application/pdf" && file.previewElement) {
        const dzImage = file.previewElement.querySelector(".dz-image");
        if (dzImage) {
          renderPdfPreview(dzImage, file);
        }
      } else if (usesGenericDocumentPreview(file, this.previewTemplateValue) && file.previewElement) {
        const dzImage = file.previewElement.querySelector(".dz-image");
        if (dzImage) {
          renderDocumentPreview(dzImage, file);
        }
      }
    });

    return dropZone;
  }

  #syncUploadBlocking() {
    const existingFiles = new Set(this.existingFiles.values());
    const blocking = this.dropZone.files.some(
      (file) => !existingFiles.has(file) && file.status !== Dropzone.SUCCESS
    );

    setUploadBlocking(this.element, blocking);
  }

  #handleFileRemoval(file) {
    this.#updateAddButtonVisibility();
    this.#createDeletionHiddenInput(file);
    this.#emitDropzoneRemovedFile(file);
  }

  #updateAddButtonVisibility() {
    if (!this.addButton || !this.maxFilesValue) {
      return;
    }

    const currentFileCount = this.dropZone.files.length;

    if (currentFileCount >= this.maxFilesValue) {
      this.addButton.style.display = "none";
    } else {
      this.addButton.style.display = "";
    }
  }

  #updateCounter() {
    if (!this.counterElement || !this.maxFilesValue) {
      return;
    }

    const currentFileCount = this.dropZone.files.length;

    this.counterElement.textContent = `(${currentFileCount}/${this.maxFilesValue})`;
  }

  #handleMaxFilesExceeded(file) {
    this.dropZone.removeFile(file);

    // Show error in the global error container (like post form validation errors)
    this.#showGlobalError(this.dictMaxFilesExceededValue);
  }

  #showGlobalError(message) {
    let errorContainer = document.querySelector(
      ".new-post__errors, .form__errors"
    );

    if (!errorContainer) {
      errorContainer = document.createElement("div");
      errorContainer.className = "form__errors";

      const errorPlaceholder = document.querySelector(
        ".js-error-placeholder"
      );
      if (errorPlaceholder) {
        errorPlaceholder.appendChild(errorContainer);
      } else {
        const form = this.element.closest("form");
        if (form) {
          form.insertBefore(errorContainer, form.firstChild);
        }
      }
    }

    // Display the error
    errorContainer.innerHTML = `
      <ul>
        <li class="body--medium">${message}</li>
      </ul>
    `;
    errorContainer.style.display = "block";

    // Scroll to errors
    errorContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  #createDeletionHiddenInput(file) {
    const isVideo = file.type && file.type.startsWith("video/");

    // Videos: create BOTH deletion inputs (attachment + video record)
    if (isVideo && file.attachment_id && this.videosDeletionParamNameValue) {
      this.#appendHiddenInput(this.deletionParamNameValue, file.attachment_id);
      this.#appendHiddenInput(this.videosDeletionParamNameValue, file.id);
    } else {
      // Regular attachments: single deletion input
      this.#appendHiddenInput(this.deletionParamNameValue, file.id);
    }
  }

  #appendHiddenInput(name, value) {
    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = name;
    hiddenInput.value = value;
    hiddenInput.classList.add("dropzone-deletion-input");
    this.element.appendChild(hiddenInput);
  }

  #upload(file) {
    new Uploader(file, this.dropZone, this.submitButton).start();
  }

  #emitDropzoneRemovedFile(file) {
    this.dropZone.emit("dropzone:removedfile", file);
  }
}

class Uploader {
  constructor(file, dropZone, submitButton) {
    this.file = file;
    this.dropZone = dropZone;
    this.submitButton = submitButton;
  }

  async start() {
    this.dropZone.emit("start", this.file);
    try {
      await refreshCsrfToken();
    } catch (error) {
      this.#emitDropzoneError("Upload failed. Please try again.");
      return;
    }

    this.#createDirectUpload((error, blob) => {
      if (error) {
        this.#emitDropzoneError(error);
      } else {
        this.#createHiddenInput(blob.signed_id);
        this.#emitDropzoneSuccess();
      }
    });
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.file.xhr = xhr;
    this.#bindProgress();
    this.#emitDropzoneProcessing();
  }

  #createDirectUpload(callback) {
    new DirectUpload(this.file, this.dropZone.options.url, this).create(
      callback
    );
  }

  #createHiddenInput(signedId) {
    this.hiddenInput = document.createElement("input");
    this.hiddenInput.type = "hidden";
    this.hiddenInput.name = this.dropZone.options.paramName;
    this.hiddenInput.value = signedId;
    this.file.previewElement.appendChild(this.hiddenInput);
  }

  #bindProgress() {
    this.file.xhr.upload.addEventListener("progress", (it) => {
      this.#directUploadDidProgress(it);
    });
  }

  #directUploadDidProgress(event) {
    this.dropZone._updateFilesUploadProgress([this.file], this.xhr, event);
  }

  #emitDropzoneProcessing() {
    this.file.status = Dropzone.PROCESSING;
    this.dropZone.emit("processing", this.file);
  }

  #emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.dropZone.emit("success", this.file);
    this.dropZone.emit("complete", this.file);
  }

  #emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.dropZone.emit("error", this.file, error);
    this.dropZone.emit("complete", this.file);
  }
}
